import React from 'react';
import { ActivityIndicator, Pressable, StyleSheet, View } from 'react-native';
import { Text } from './Texte';
import { Check, Ionicons } from './icons';
import { Button } from './Button';
import { GradientButton } from './GradientButton';
import type { LinkedinState } from '../hooks/useLinkedinConnection';
import { colors, formes, radius, shadow, spacing, typography } from '../theme/tokens';
import { FEATURE_ACCENTS } from '../theme/featureAccents';
import { t } from '../i18n';


// Dit AVANT toute tentative que la zone conditionne la connexion, et qu'il existe une autre voie.
// useLinkedinConnection ne pose 'unavailable' que dans connect() : sans cette ligne, une personne
// hors UE ne découvrirait la restriction qu'APRÈS avoir tapé le bouton et échoué.


// Bloc de réassurance sous le bouton : ce que la connexion donne accès, et rien de plus.
function SecurityNote() {
  return (
    <View style={styles.secure} testID="li-secure">
      <Ionicons name="shield-checkmark" size={18} color={colors.violet} />
      <View style={styles.secureTexts}>
        <Text style={styles.secureTitle}>{t('linkedin.connection.secureTitle')}</Text>
        <Text style={styles.secureBody}>{t('linkedin.connection.secureBody')}</Text>
      </View>
    </View>
  );
}

interface LinkedinConnectionCardProps {
  state: LinkedinState;
  busy: boolean;
  connect: () => void;
  disconnect: () => void;
  error?: string | null; // échec de déconnexion remonté par le hook
}

// Bloc connexion LinkedIn (OAuth Member Data Portability) en tête de l'écran d'audit. Présentationnel :
// l'état est piloté par useLinkedinConnection dans l'écran (partagé avec le bouton d'analyse compte).
// Connexion persistante ; déconnexion in-context (retrait de consentement RGPD). Le token vit côté
// serveur. Les captures de l'audit ne dépendent PAS de ce bloc.
export function LinkedinConnectionCard({ state, busy, connect, disconnect, error }: LinkedinConnectionCardProps) {
  if (state === 'loading') {
    return (
      <View style={styles.card} testID="li-conn-card">
        <ActivityIndicator testID="li-conn-loading" color={colors.electric} />
      </View>
    );
  }

  return (
    <View style={styles.card} testID="li-conn-card">
      {state === 'connected' ? (
        // Une seule ligne : pastille verte, ce qui est connecté, et « Déconnecter » en lien. Le retrait
        // du consentement reste à portée de pouce (44 pt de haut), sans bouton plein qui l'inviterait.
        <View style={styles.ligne}>
          <View style={styles.pastilleOk}>
            <Check size={20} color={colors.success} strokeWidth={3} />
          </View>
          <View style={styles.ligneTextes}>
            <Text style={styles.statusOk} testID="li-conn-status">
              {t('linkedin.connection.connected')}
            </Text>
            <Text style={styles.ligneSous}>{t('linkedin.connection.connectedSub')}</Text>
          </View>
          <Pressable
            testID="li-disconnect"
            accessibilityRole="button"
            accessibilityState={{ disabled: busy, busy }}
            disabled={busy}
            onPress={disconnect}
            style={styles.deconnecter}
          >
            {busy ? (
              <ActivityIndicator color={FEATURE_ACCENTS.linkedin.ink} />
            ) : (
              <Text style={styles.deconnecterTexte}>{t('linkedin.connection.disconnect')}</Text>
            )}
          </Pressable>
        </View>
      ) : state === 'expired' ? (
        <>
          <Text style={styles.statusWarn} testID="li-conn-status">
            {t('linkedin.connection.expired')}
          </Text>
          <GradientButton label={t('linkedin.connection.reconnect')} onPress={connect} loading={busy} testID="li-reconnect" />
          <Button title={t('linkedin.connection.disconnect')} variant="secondary" onPress={disconnect} testID="li-disconnect" />
        </>
      ) : state === 'unavailable' ? (
        <Text style={styles.statusInfo} testID="li-conn-status">
          {`${t('linkedin.connection.unavailable')} ${t('linkedin.connection.fallbackHint')}`}
        </Text>
      ) : state === 'unknown' ? (
        // Statut illisible : on ignore s'il existe un token côté serveur. Les deux actions restent
        // ouvertes — se déconnecter doit rester possible « à tout moment » (RGPD).
        <>
          <Text style={styles.statusWarn} testID="li-conn-status">
            {`${t('linkedin.connection.unknown')} ${t('linkedin.connection.fallbackHint')}`}
          </Text>
          <GradientButton label={t('linkedin.connection.connect')} onPress={connect} loading={busy} testID="li-connect" />
          <Button title={t('linkedin.connection.disconnect')} variant="secondary" onPress={disconnect} testID="li-disconnect" />
        </>
      ) : (
        // disconnected / error
        <>
          {state === 'error' ? (
            <Text style={styles.statusErr} testID="li-conn-status">
              {t('linkedin.connection.failed')}
            </Text>
          ) : null}
          <GradientButton
            label={t('linkedin.connection.connect')}
            icon={<Ionicons name="logo-linkedin" size={20} color={colors.white} />}
            onPress={connect}
            loading={busy}
            testID="li-connect"
          />
          <SecurityNote />
          <Text style={styles.hint}>{t('linkedin.connection.eligibilityHint')}</Text>
        </>
      )}

      {error ? (
        <Text style={styles.statusErr} testID="li-conn-error">
          {error}
        </Text>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    gap: spacing.sm,
    padding: spacing.lg,
    borderRadius: formes.rayon.lg,
    backgroundColor: colors.white,
    ...shadow.card,
  },
  ligne: { flexDirection: 'row', alignItems: 'center', gap: spacing.md, minHeight: 50 },
  pastilleOk: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: colors.successSoft,
    alignItems: 'center',
    justifyContent: 'center',
  },
  ligneTextes: { flex: 1, gap: 2 },
  ligneSous: { fontSize: 12.5, lineHeight: 16, color: colors.grey500 },
  deconnecter: { minHeight: formes.cible, minWidth: formes.cible, justifyContent: 'center', alignItems: 'flex-end' },
  deconnecterTexte: { fontSize: 13, fontWeight: '800', color: FEATURE_ACCENTS.linkedin.ink },
  statusOk: { fontSize: 15, fontWeight: '800', color: colors.night },
  statusWarn: { fontSize: typography.small.size, color: colors.grey700 },
  statusInfo: { fontSize: typography.small.size, color: colors.grey700 },
  statusErr: { fontSize: typography.small.size, color: colors.error },
  hint: { fontSize: typography.small.size, lineHeight: typography.small.lineHeight, color: colors.grey500 },

  secure: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.sm,
    padding: spacing.md,
    borderRadius: radius.md,
    backgroundColor: colors.grey50,
  },
  secureTexts: { flex: 1, gap: spacing.xs },
  secureTitle: {
    fontSize: typography.small.size,
    fontWeight: typography.bodyMd.weight,
    color: colors.violet,
  },
  secureBody: {
    fontSize: typography.small.size,
    lineHeight: typography.small.lineHeight,
    color: colors.grey700,
  },
});
